import { motion, Variants } from "framer-motion";
import { ease, viewportOnce } from "@/lib/motion";

/**
 * EditorialSteps - Enterprise Process Section
 * 
 * Design System:
 * - Large step numbers as visual anchors (low opacity)
 * - Content-first hierarchy
 * - Thin horizontal dividers
 * - No icons, timelines, or gimmicks
 * 
 * Animation:
 * - Section header: fade + subtle upward movement
 * - Step numbers: fade only (stable, authoritative)
 * - Step content: fade + slight horizontal slide from left
 * - Staggered reveal for calm reading rhythm
 */

export interface Step {
  number: string;
  title: string;
  description: string;
}

interface EditorialStepsProps {
  title: string;
  steps: Step[];
  label?: string;
}

// Section header animation - fade + subtle upward movement
const headerVariant: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: ease,
    },
  },
};

// Step number - fade only, no movement (stable and authoritative)
const stepNumberVariant: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.45,
      ease: ease,
    },
  },
};

// Step content - fade + slight horizontal slide from left
const stepContentVariant: Variants = {
  hidden: { opacity: 0, x: -16 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.45,
      ease: ease,
    },
  },
};

// Stagger container for steps
const stepsStaggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1,
    },
  },
};

// Individual step container (for stagger timing)
const stepItemVariant: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const EditorialSteps = ({ 
  title, 
  steps, 
  label = "Process" 
}: EditorialStepsProps) => {
  return (
    <section className="py-10 md:py-14 lg:py-20 border-t border-border">
      <div className="container-wide">
        {/* Section Header */}
        <motion.div
          className="max-w-3xl mb-8 md:mb-10 lg:mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={headerVariant}
        >
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">
            {label}
          </span>
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground">
            {title}
          </h2>
        </motion.div>

        {/* Steps */}
        <motion.div
          className="space-y-0"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={stepsStaggerContainer}
        >
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              variants={stepItemVariant}
              className="border-t border-border"
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 py-5 md:py-10 lg:py-12">
                {/* Step Number - Left Column */}
                <motion.div
                  className="md:col-span-2"
                  variants={stepNumberVariant}
                >
                  <span className="font-display text-5xl md:text-6xl lg:text-7xl font-semibold text-foreground/10 leading-none">
                    {step.number}
                  </span>
                </motion.div>

                {/* Step Content - Right Column */}
                <motion.div
                  className="md:col-span-10 md:pt-2"
                  variants={stepContentVariant}
                >
                  <h3 className="text-lg md:text-xl font-medium text-foreground mb-3">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed max-w-2xl">
                    {step.description}
                  </p>
                </motion.div>
              </div>
            </motion.div>
          ))}
          {/* Bottom border */}
          <div className="border-t border-border" />
        </motion.div>
      </div>
    </section>
  );
};

export default EditorialSteps;
